<div data-spartan-page
     class="p-6 md:p-8 space-y-5"
     style="background: rgb(var(--ds-bg, 250 250 249)); min-height: 100%;"
     [class.fixed]="editorExpanded"
     [class.inset-0]="editorExpanded"
     [class.z-50]="editorExpanded"
     [class.overflow-auto]="editorExpanded">

  <!-- Page header -->
  @if (template) {
    <app-page-header
      eyebrow="Forwarding / Admin / Templates"
      [title]="template.name"
    >
      <div class="flex items-center gap-2 flex-wrap" subtitle>
        <span class="font-mono text-[12px] font-semibold bg-ds-accent-light text-ds-accent px-2 py-0.5 rounded-md">
          {{ template.shipmentMode }}
        </span>
        @if (template.containerLoadType) {
          <span class="font-mono text-[12px] font-semibold bg-muted text-muted-foreground px-2 py-0.5 rounded-md">
            {{ template.containerLoadType }}
          </span>
        }
        <app-status-badge [variant]="getStatusVariant(template.status)" [label]="template.status | titlecase" />
      </div>
      <div class="flex items-center gap-2" actions>
        <button hlmBtn variant="outline" class="gap-2 h-9" (click)="previewTemplate()" [disabled]="saving">
          <ng-icon hlm name="lucideEye" class="size-4" />
          Preview
        </button>
        <button hlmBtn class="gap-2 h-9" (click)="saveTemplate()" [disabled]="saving || loading">
          <ng-icon hlm name="lucideSave" class="size-4" />
          @if (saving) { Saving... } @else { Save Changes }
        </button>
      </div>
    </app-page-header>
  } @else if (!loading) {
    <app-page-header
      eyebrow="Forwarding / Admin / Templates"
      title="Edit Template"
    >
      <div actions>
        <button hlmBtn variant="outline" class="gap-2 h-9" (click)="goBack()">
          <ng-icon hlm name="lucideArrowLeft" class="size-4" />
          Back
        </button>
      </div>
    </app-page-header>
  }

  <!-- Loading skeleton -->
  @if (loading) {
    <div class="rounded-lg border border-border bg-card shadow-sm p-8 text-center text-sm text-muted-foreground">
      Loading template...
    </div>
  }

  <!-- Main content -->
  @if (!loading) {

    <!-- Tab bar -->
    <div class="rounded-lg border border-border bg-card shadow-sm">
      <div class="border-b border-border px-4">
        <nav class="flex gap-1 -mb-px" role="tablist">
          <button
            role="tab"
            [attr.aria-selected]="activeTab === 'settings'"
            class="flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors"
            [class.border-ds-accent]="activeTab === 'settings'"
            [class.text-ds-accent]="activeTab === 'settings'"
            [class.border-transparent]="activeTab !== 'settings'"
            [class.text-muted-foreground]="activeTab !== 'settings'"
            [class.hover:text-foreground]="activeTab !== 'settings'"
            (click)="setActiveTab('settings')"
          >
            <ng-icon hlm name="lucideSettings" class="size-4" />
            Template Settings
          </button>
          <button
            role="tab"
            [attr.aria-selected]="activeTab === 'charges'"
            class="flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors"
            [class.border-ds-accent]="activeTab === 'charges'"
            [class.text-ds-accent]="activeTab === 'charges'"
            [class.border-transparent]="activeTab !== 'charges'"
            [class.text-muted-foreground]="activeTab !== 'charges'"
            [class.hover:text-foreground]="activeTab !== 'charges'"
            (click)="setActiveTab('charges')"
          >
            <ng-icon hlm name="lucideReceipt" class="size-4" />
            Charges
          </button>
          <button
            role="tab"
            [attr.aria-selected]="activeTab === 'html'"
            class="flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors"
            [class.border-ds-accent]="activeTab === 'html'"
            [class.text-ds-accent]="activeTab === 'html'"
            [class.border-transparent]="activeTab !== 'html'"
            [class.text-muted-foreground]="activeTab !== 'html'"
            [class.hover:text-foreground]="activeTab !== 'html'"
            (click)="setActiveTab('html')"
          >
            <ng-icon hlm name="lucideCode2" class="size-4" />
            HTML Template
          </button>
        </nav>
      </div>

      <!-- ── Settings Tab ── -->
      @if (activeTab === 'settings') {
        <div class="p-6 max-w-2xl space-y-5">

          <div class="grid gap-1.5">
            <label hlmLabel>Template Name <span class="text-destructive">*</span></label>
            <input hlmInput [(ngModel)]="editForm.name" placeholder="Enter template name" />
          </div>

          <div class="grid gap-1.5">
            <label hlmLabel>Shipment Mode <span class="text-destructive">*</span></label>
            <select [(ngModel)]="editForm.shipmentMode" [class]="selectClass">
              <option value="SEA">Sea Freight</option>
              <option value="AIR">Air Freight</option>
              <option value="LAND">Land Transport</option>
            </select>
          </div>

          <div class="grid gap-1.5">
            <label hlmLabel>Container Load Type</label>
            <select [(ngModel)]="editForm.containerLoadType" [class]="selectClass">
              <option value="">— Select container load type —</option>
              @for (type of containerLoadTypes; track type.code) {
                <option [value]="type.code">{{ type.code }} — {{ type.label }}</option>
              }
            </select>
          </div>

          <div class="grid gap-1.5">
            <label hlmLabel>Incoterm Scope</label>
            <select [(ngModel)]="editForm.incotermScope" [class]="selectClass">
              <option value="">— Select incoterm scope —</option>
              @for (incoterm of incoterms; track incoterm.code) {
                <option [value]="incoterm.code">{{ incoterm.code }} — {{ incoterm.description }}</option>
              }
            </select>
          </div>

          <div class="grid gap-1.5">
            <label hlmLabel>Status</label>
            <select [(ngModel)]="editForm.status" [class]="selectClass">
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div class="flex items-center gap-2 pt-1">
            <input
              type="checkbox"
              id="setAsDefault"
              [(ngModel)]="editForm.isDefault"
              class="h-4 w-4 rounded border-input text-ds-accent focus:ring-ds-accent"
            />
            <label for="setAsDefault" class="text-sm font-medium leading-none cursor-pointer">
              Set as default template
            </label>
          </div>

        </div>
      }

      <!-- ── Charges Tab ── -->
      @if (activeTab === 'charges') {
        <div class="p-6">
          <div class="grid md:grid-cols-2 gap-6">

            <!-- Default Charge Codes -->
            <div class="rounded-lg border border-border bg-ds-surface-raised/40 p-5 space-y-3">
              <div>
                <p class="text-sm font-semibold">Default Charge Codes</p>
                <p class="text-xs text-muted-foreground mt-0.5">Select the standard charges to include in this template</p>
              </div>
              <div class="space-y-2">
                @for (charge of defaultChargeCodes; track charge.code) {
                  <div class="flex items-center gap-2">
                    <input
                      type="checkbox"
                      [id]="'charge-' + charge.code"
                      [checked]="isChargeCodeSelected(charge.code)"
                      (change)="toggleChargeCode(charge.code)"
                      class="h-4 w-4 rounded border-input text-ds-accent focus:ring-ds-accent"
                    />
                    <label [for]="'charge-' + charge.code" class="text-sm cursor-pointer">
                      <span class="font-mono text-[12px] font-semibold bg-ds-accent-light text-ds-accent px-1.5 py-0.5 rounded mr-1">{{ charge.code }}</span>
                      {{ charge.label }}
                    </label>
                  </div>
                }
              </div>
            </div>

            <!-- Operational Charges -->
            <div class="rounded-lg border border-border bg-ds-surface-raised/40 p-5 space-y-3">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm font-semibold">Operational Charges</p>
                  <p class="text-xs text-muted-foreground mt-0.5">Select operational charges from your system</p>
                </div>
                <button hlmBtn variant="ghost" size="icon-sm" (click)="refreshOperationalCharges()" title="Refresh charges">
                  <ng-icon hlm name="lucideRefreshCw" class="size-4" />
                </button>
              </div>

              <div class="relative">
                <ng-icon hlm name="lucideSearch" class="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
                <input hlmInput
                       class="pl-8 h-8 text-sm"
                       placeholder="Search charges..."
                       [(ngModel)]="operationalChargeSearchTerm"
                       (ngModelChange)="filterOperationalCharges()" />
              </div>

              <div class="space-y-2 max-h-64 overflow-y-auto pr-1">
                @for (charge of filteredOperationalCharges; track charge.id) {
                  <div class="flex items-start gap-2 rounded-md border border-border bg-card p-3">
                    <input
                      type="checkbox"
                      [id]="'op-charge-' + charge.id"
                      [checked]="isOperationalChargeSelected(charge.id)"
                      (change)="toggleOperationalCharge(charge.id)"
                      class="mt-0.5 h-4 w-4 rounded border-input text-ds-accent focus:ring-ds-accent"
                    />
                    <label [for]="'op-charge-' + charge.id" class="flex-1 cursor-pointer">
                      <div class="text-sm font-medium">{{ charge.feeName }}</div>
                      <div class="text-xs text-muted-foreground mt-0.5">
                        {{ charge.chargeBasis }} · {{ charge.mode.replace('_', ' ') }} · {{ charge.defaultPrice }} {{ charge.currency }}
                      </div>
                    </label>
                    <span class="font-mono text-[11px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded shrink-0">
                      {{ charge.mode.replace('SEA_', '').replace('_', ' ') }}
                    </span>
                  </div>
                }
                @if (filteredOperationalCharges.length === 0) {
                  <p class="text-sm text-muted-foreground text-center py-4">No charges found</p>
                }
              </div>
            </div>

          </div>
        </div>
      }

      <!-- ── HTML Template Tab ── -->
      @if (activeTab === 'html') {

        <!-- Fullscreen overlay -->
        @if (editorExpanded) {
          <div class="fixed inset-0 z-50 bg-background flex flex-col">
            <!-- Fullscreen header -->
            <div class="flex items-center justify-between border-b border-border bg-card px-5 py-3 shrink-0">
              <div class="flex items-center gap-2">
                <ng-icon hlm name="lucideCode2" class="size-4 text-ds-accent" />
                <span class="text-sm font-semibold">HTML Template Editor</span>
              </div>
              <div class="flex items-center gap-2">
                <button hlmBtn variant="outline" size="sm" class="gap-1.5 h-8" (click)="togglePlaceholders()">
                  <ng-icon hlm name="lucideList" class="size-3.5" />
                  {{ showPlaceholders ? 'Hide' : 'Show' }} Placeholders
                </button>
                <button hlmBtn variant="outline" size="sm" class="gap-1.5 h-8" (click)="toggleExpandEditor()">
                  <ng-icon hlm name="lucideMinimize2" class="size-3.5" />
                  Exit Fullscreen
                </button>
              </div>
            </div>

            <!-- Fullscreen body -->
            <div class="flex flex-1 overflow-hidden">
              <div class="flex-1 flex flex-col p-4">
                <textarea
                  class="flex-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono resize-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  [(ngModel)]="editForm.htmlContent"
                  placeholder="Enter HTML template content..."></textarea>
              </div>

              @if (showPlaceholders) {
                <div class="w-72 border-l border-border bg-ds-surface-raised/40 overflow-y-auto p-4 shrink-0">
                  <ng-container *ngTemplateOutlet="placeholdersList" />
                </div>
              }
            </div>
          </div>
        }

        <!-- Normal view -->
        @if (!editorExpanded) {
          <div class="p-6">
            <div class="flex gap-4">
              <div class="flex-1 space-y-3">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <ng-icon hlm name="lucideCode2" class="size-4 text-ds-accent" />
                    <span class="text-sm font-semibold">HTML Template Editor</span>
                  </div>
                  <div class="flex items-center gap-2">
                    <button hlmBtn variant="outline" size="sm" class="gap-1.5 h-8" (click)="togglePlaceholders()">
                      <ng-icon hlm name="lucideList" class="size-3.5" />
                      {{ showPlaceholders ? 'Hide' : 'Show' }} Placeholders
                    </button>
                    <button hlmBtn variant="outline" size="sm" class="gap-1.5 h-8" (click)="toggleExpandEditor()">
                      <ng-icon hlm name="lucideMaximize2" class="size-3.5" />
                      Expand
                    </button>
                  </div>
                </div>

                <textarea
                  class="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono resize-y focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  rows="20"
                  [(ngModel)]="editForm.htmlContent"
                  placeholder="Enter HTML template content..."></textarea>
              </div>

              <!-- Placeholders sidebar -->
              @if (showPlaceholders) {
                <div class="w-64 shrink-0 rounded-lg border border-border bg-ds-surface-raised/40 overflow-y-auto p-4 self-start sticky top-4">
                  <ng-container *ngTemplateOutlet="placeholdersList" />
                </div>
              }
            </div>
          </div>
        }

      }
    </div>

  }

</div>

<!-- ── Placeholders reference panel (shared between normal + fullscreen) ── -->
<ng-template #placeholdersList>
  <div class="space-y-4">
    <div class="flex items-center gap-2 pb-2 border-b border-border">
      <ng-icon hlm name="lucideHelpCircle" class="size-3.5 text-ds-accent" />
      <span class="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Available Placeholders</span>
    </div>

    @if (editForm.operationalChargeIds.length > 0) {
      <div>
        <p class="text-xs font-semibold text-ds-accent mb-1.5">Selected Operational Charges</p>
        <div class="space-y-1">
          @for (charge of getSelectedOperationalChargesForDisplay(); track charge.id) {
            <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">
              {{ '{{operational_charge.' + charge.chargeCode + '}}' }}
            </code>
          }
        </div>
      </div>
    } @else {
      <p class="text-xs text-muted-foreground italic">No charges selected — go to the Charges tab to add some.</p>
    }

    <div>
      <p class="text-xs font-semibold text-ds-accent mb-1.5">Client</p>
      <div class="space-y-0.5">
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{client.name}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{client.address}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{client.email}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{client.phone}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{client.preferred_currency}}'}}</code>
      </div>
    </div>

    <div>
      <p class="text-xs font-semibold text-ds-accent mb-1.5">Agent</p>
      <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{agent.name}}'}}</code>
    </div>

    <div>
      <p class="text-xs font-semibold text-ds-accent mb-1.5">Incoterm</p>
      <div class="space-y-0.5">
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{incoterm.code}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{incoterm.description}}'}}</code>
      </div>
    </div>

    <div>
      <p class="text-xs font-semibold text-ds-accent mb-1.5">Ports</p>
      <div class="space-y-0.5">
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{ports.origin.code}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{ports.origin.name}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{ports.destination.code}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{ports.destination.name}}'}}</code>
      </div>
    </div>

    <div>
      <p class="text-xs font-semibold text-ds-accent mb-1.5">Cargo</p>
      <div class="space-y-0.5">
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{cargo.hs_code}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{cargo.gross_weight}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{cargo.chargeable_weight}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{cargo.commodity}}'}}</code>
      </div>
    </div>

    <div>
      <p class="text-xs font-semibold text-ds-accent mb-1.5">Shipment</p>
      <div class="space-y-0.5">
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{shipment.mode}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{shipment.container_load_type}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{shipment.container_size}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{shipment.container_count}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{shipment.transit_days}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{shipment.free_storage_days}}'}}</code>
      </div>
    </div>

    <div>
      <p class="text-xs font-semibold text-ds-accent mb-1.5">Carrier</p>
      <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{carrier.name}}'}}</code>
    </div>

    <div>
      <p class="text-xs font-semibold text-ds-accent mb-1.5">Charges</p>
      <div class="space-y-0.5">
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{charges.items[i].code}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{charges.items[i].label}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{charges.items[i].basis}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{charges.items[i].unit_rate}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{charges.items[i].currency}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{charges.items[i].amount}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{charges.items[i].included}}'}}</code>
      </div>
    </div>

    <div>
      <p class="text-xs font-semibold text-ds-accent mb-1.5">Totals</p>
      <div class="space-y-0.5">
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{totals.currency}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{totals.subtotal}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{totals.vat}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{totals.grand_total}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{totals.exchange_rate}}'}}</code>
      </div>
    </div>

    <div>
      <p class="text-xs font-semibold text-ds-accent mb-1.5">Meta</p>
      <div class="space-y-0.5">
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{meta.quote_number}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{meta.date_issued}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{meta.date_issued_display}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{meta.valid_until_display}}'}}</code>
        <code class="block text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{{'{{meta.notes}}'}}</code>
      </div>
    </div>
  </div>
</ng-template>
